/**
 * Admin Analytics Dashboard
 * Shows website traffic, countries, pages, devices
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, 
  Globe, 
  Monitor, 
  Smartphone, 
  Tablet,
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  Clock,
  RefreshCw,
  FileText,
  Chrome,
  Compass,
  MousePointer2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Country flag emoji from country code
const getFlag = (code) => {
  if (!code || code === "XX") return "🌍";
  const codePoints = code
    .toUpperCase()
    .split("")
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// Device icon
const DeviceIcon = ({ device }) => {
  switch (device) {
    case "Mobile": return <Smartphone className="w-4 h-4" />;
    case "Tablet": return <Tablet className="w-4 h-4" />;
    default: return <Monitor className="w-4 h-4" />;
  }
};

// Browser icon
const BrowserIcon = ({ browser }) => {
  switch (browser) {
    case "Chrome": return <Chrome className="w-4 h-4" />;
    case "Safari": return <Compass className="w-4 h-4" />;
    default: return <Globe className="w-4 h-4" />;
  }
};

// Format time spent in human readable format
const formatTimeSpent = (seconds) => {
  if (!seconds || seconds <= 0) return "-";
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
};

// Detect if visitor is a bot or real user
const detectVisitorType = (visit) => {
  const ip = visit.ip || "";
  const city = visit.city || "";
  const browser = visit.browser || "";
  const time = visit.total_time_spent || 0;
  
  // Admin IP(s) - add your IP here
  const adminIPs = ["81.95.83.19"];
  
  if (adminIPs.includes(ip)) {
    return { type: "admin", label: "ADMIN", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" };
  }
  
  // Known bot IP ranges
  const botPatterns = [
    /^66\.249\./, // Googlebot
    /^74\.125\./, // Google
    /^192\.178\./, // Google
    /^35\./, // Google Cloud
    /^34\./, // Google Cloud
    /^64\.233\./, // Google
    /^72\.14\./, // Google
    /^209\.85\./, // Google
    /^216\.239\./, // Google
  ];
  
  // Check if IP matches bot pattern
  const isKnownBot = botPatterns.some(pattern => pattern.test(ip));
  
  // Mountain View = Google HQ
  const isMountainView = city.toLowerCase().includes("mountain view");
  
  if (isKnownBot || isMountainView) {
    return { type: "bot", label: "Bot", color: "bg-red-500/20 text-red-400 border-red-500/30" };
  }
  
  // Has time = definitely real
  if (time > 0) {
    return { type: "real", label: "Real", color: "bg-green-500/20 text-green-400 border-green-500/30" };
  }
  
  // Safari without time = real but tracking blocked
  if (browser === "Safari") {
    return { type: "safari", label: "Real", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", note: "Safari" };
  }
  
  // Unknown - probably real visitor who bounced quickly
  return { type: "unknown", label: "?", color: "bg-gray-500/20 text-gray-400 border-gray-500/30" };
};

export default function AdminAnalytics() {
  const [summary, setSummary] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [visitLimit, setVisitLimit] = useState(50);
  const [periodFilter, setPeriodFilter] = useState("all"); // all, today, week, month

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("scaffoldx_token");
      console.log("Fetching analytics with token:", token ? "present" : "missing");
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, recentRes] = await Promise.all([
        fetch(`${API}/api/analytics/summary?days=${days}`, { headers }),
        fetch(`${API}/api/analytics/recent?limit=${visitLimit}`, { headers })
      ]);

      console.log("Summary response status:", summaryRes.status);
      console.log("Recent response status:", recentRes.status);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        console.log("Summary data:", data);
        setSummary(data);
      } else {
        console.error("Summary fetch failed:", await summaryRes.text());
      }
      if (recentRes.ok) {
        const data = await recentRes.json();
        console.log("Recent data:", data);
        setRecentVisits(data);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [days, visitLimit]);

  // Filter visits by period
  const filteredVisits = recentVisits.filter(visit => {
    if (periodFilter === "all") return true;
    
    const visitDate = new Date(visit.timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    switch (periodFilter) {
      case "today":
        return visitDate >= today;
      case "week":
        return visitDate >= weekAgo;
      case "month":
        return visitDate >= monthAgo;
      default:
        return true;
    }
  });

  // Calculate statistics from filtered visits
  const stats = {
    totalVisits: filteredVisits.length,
    realUsers: filteredVisits.filter(v => detectVisitorType(v).type === "real").length,
    avgTimeOnSite: filteredVisits.length > 0 
      ? Math.round(filteredVisits.reduce((sum, v) => sum + (v.total_time_spent || 0), 0) / filteredVisits.length)
      : 0,
    maxScroll100: filteredVisits.filter(v => v.max_scroll >= 100).length,
    scrollRate: filteredVisits.length > 0 
      ? Math.round((filteredVisits.filter(v => v.max_scroll >= 50).length / filteredVisits.length) * 100)
      : 0,
    bounceRate: filteredVisits.length > 0
      ? Math.round((filteredVisits.filter(v => (v.total_time_spent || 0) < 10 && (v.max_scroll || 0) < 25).length / filteredVisits.length) * 100)
      : 0
  };

  const changePercent = summary ? 
    summary.visits_yesterday > 0 
      ? Math.round(((summary.visits_today - summary.visits_yesterday) / summary.visits_yesterday) * 100)
      : summary.visits_today > 0 ? 100 : 0
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Unbounded']">Analytics</h1>
          <p className="text-[#94A3B8]">Trafic site și statistici vizitatori</p>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value={7}>Ultimele 7 zile</option>
            <option value={30}>Ultimele 30 zile</option>
            <option value={90}>Ultimele 90 zile</option>
          </select>
          <Button 
            onClick={fetchAnalytics} 
            variant="outline" 
            className="border-white/20 text-white hover:bg-white/10"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading && !summary ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-[#23F7DD] animate-spin" />
        </div>
      ) : summary ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-[#23F7DD]/20 to-[#23F7DD]/5 border-[#23F7DD]/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#94A3B8] text-sm">Vizite Azi</p>
                    <p className="text-3xl font-bold text-white">{summary.visits_today}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {changePercent >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-400" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-400" />
                      )}
                      <span className={changePercent >= 0 ? "text-green-400 text-sm" : "text-red-400 text-sm"}>
                        {changePercent >= 0 ? "+" : ""}{changePercent}% vs ieri
                      </span>
                    </div>
                  </div>
                  <Users className="w-10 h-10 text-[#23F7DD]/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#4F46E5]/20 to-[#4F46E5]/5 border-[#4F46E5]/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#94A3B8] text-sm">Săptămâna Asta</p>
                    <p className="text-3xl font-bold text-white">{summary.visits_week}</p>
                    <p className="text-[#94A3B8] text-sm mt-1">vizite unice</p>
                  </div>
                  <BarChart3 className="w-10 h-10 text-[#4F46E5]/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#F7931A]/20 to-[#F7931A]/5 border-[#F7931A]/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#94A3B8] text-sm">Total Vizite ({days} zile)</p>
                    <p className="text-3xl font-bold text-white">{summary.total_visits}</p>
                    <p className="text-[#94A3B8] text-sm mt-1">{summary.total_pageviews || 0} pagini văzute</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-[#F7931A]/50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#10b981]/20 to-[#10b981]/5 border-[#10b981]/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#94A3B8] text-sm">Vizitatori Unici</p>
                    <p className="text-3xl font-bold text-white">{summary.unique_visitors}</p>
                    <p className="text-[#94A3B8] text-sm mt-1">IP-uri diferite</p>
                  </div>
                  <Eye className="w-10 h-10 text-[#10b981]/50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Countries */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#23F7DD]" />
                  Top Țări
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {summary.top_countries.length > 0 ? (
                    summary.top_countries.map((country, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getFlag(country.country_code)}</span>
                          <span className="text-white">{country.country}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#23F7DD]" 
                              style={{ width: `${(country.count / summary.top_countries[0].count) * 100}%` }}
                            />
                          </div>
                          <span className="text-[#94A3B8] text-sm w-12 text-right">{country.count}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-[#94A3B8] text-center py-4">Încă nu sunt date</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top Pages */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#4F46E5]" />
                  Top Pagini
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {summary.top_pages.length > 0 ? (
                    summary.top_pages.map((page, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-white truncate max-w-[200px]" title={page.page}>
                          {page.page === "/" ? "Homepage" : page.page}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#4F46E5]" 
                              style={{ width: `${(page.count / summary.top_pages[0].count) * 100}%` }}
                            />
                          </div>
                          <span className="text-[#94A3B8] text-sm w-12 text-right">{page.count}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-[#94A3B8] text-center py-4">Încă nu sunt date</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Device & Browser Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Devices */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-[#F7931A]" />
                  Dispozitive
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  {summary.device_breakdown.length > 0 ? (
                    summary.device_breakdown.map((device, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white/5 px-4 py-3 rounded-lg">
                        <DeviceIcon device={device.device} />
                        <div>
                          <p className="text-white font-medium">{device.device}</p>
                          <p className="text-[#94A3B8] text-sm">{device.count} vizite</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-[#94A3B8]">Încă nu sunt date</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Browsers */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Chrome className="w-5 h-5 text-[#10b981]" />
                  Browsere
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  {summary.browser_breakdown.length > 0 ? (
                    summary.browser_breakdown.map((browser, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white/5 px-4 py-3 rounded-lg">
                        <BrowserIcon browser={browser.browser} />
                        <div>
                          <p className="text-white font-medium">{browser.browser}</p>
                          <p className="text-[#94A3B8] text-sm">{browser.count} vizite</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-[#94A3B8]">Încă nu sunt date</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Visits - Controls */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#23F7DD]" />
                  Vizite Recente
                </CardTitle>
                
                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Period Filter */}
                  <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                    {[
                      { value: "all", label: "Toate" },
                      { value: "today", label: "Azi" },
                      { value: "week", label: "Săptămână" },
                      { value: "month", label: "Lună" }
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => setPeriodFilter(value)}
                        className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                          periodFilter === value 
                            ? "bg-[#23F7DD] text-black font-medium" 
                            : "text-white/60 hover:text-white hover:bg-white/10"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Limit Selector */}
                  <select
                    value={visitLimit}
                    onChange={(e) => setVisitLimit(Number(e.target.value))}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm"
                  >
                    <option value={50}>Ultimele 50</option>
                    <option value={100}>Ultimele 100</option>
                    <option value={200}>Ultimele 200</option>
                    <option value={500}>Ultimele 500</option>
                  </select>
                </div>
              </div>
            </CardHeader>

            {/* Quick Stats from Filtered Visits */}
            <div className="px-6 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{stats.totalVisits}</p>
                  <p className="text-xs text-[#94A3B8]">Total vizite</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3 text-center border border-green-500/20">
                  <p className="text-2xl font-bold text-green-400">{stats.realUsers}</p>
                  <p className="text-xs text-[#94A3B8]">Utilizatori reali</p>
                </div>
                <div className="bg-[#4F46E5]/10 rounded-lg p-3 text-center border border-[#4F46E5]/20">
                  <p className="text-2xl font-bold text-[#A5B4FC]">{formatTimeSpent(stats.avgTimeOnSite)}</p>
                  <p className="text-xs text-[#94A3B8]">Timp mediu</p>
                </div>
                <div className="bg-[#F7931A]/10 rounded-lg p-3 text-center border border-[#F7931A]/20">
                  <p className="text-2xl font-bold text-[#F7931A]">{stats.scrollRate}%</p>
                  <p className="text-xs text-[#94A3B8]">Scroll 50%+</p>
                </div>
                <div className="bg-[#23F7DD]/10 rounded-lg p-3 text-center border border-[#23F7DD]/20">
                  <p className="text-2xl font-bold text-[#23F7DD]">{stats.maxScroll100}</p>
                  <p className="text-xs text-[#94A3B8]">Scroll 100%</p>
                </div>
                <div className="bg-red-500/10 rounded-lg p-3 text-center border border-red-500/20">
                  <p className="text-2xl font-bold text-red-400">{stats.bounceRate}%</p>
                  <p className="text-xs text-[#94A3B8]">Bounce rate</p>
                </div>
              </div>
            </div>

            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-[#94A3B8] text-sm border-b border-white/20 bg-white/5">
                      <th className="pb-3 pt-3 pl-3 font-semibold">IP</th>
                      <th className="pb-3 pt-3 font-semibold">Țară / Oraș</th>
                      <th className="pb-3 pt-3 font-semibold">Pagini</th>
                      <th className="pb-3 pt-3 font-semibold">Scroll</th>
                      <th className="pb-3 pt-3 font-semibold">Timp</th>
                      <th className="pb-3 pt-3 font-semibold">Dispozitiv</th>
                      <th className="pb-3 pt-3 pr-3 font-semibold">Când</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisits.length > 0 ? (
                      filteredVisits.map((visit, i) => (
                        <tr 
                          key={i} 
                          className={`border-b border-white/5 text-sm transition-colors hover:bg-white/10 ${
                            i % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent'
                          } ${visit.total_time_spent > 60 ? 'border-l-2 border-l-green-500/50' : ''}`}
                        >
                          <td className="py-3 pl-3">
                            <code className="text-[#23F7DD] bg-[#23F7DD]/10 px-2 py-1 rounded text-xs font-mono border border-[#23F7DD]/20">
                              {visit.ip || "N/A"}
                            </code>
                          </td>
                          <td className="py-3">
                            <span className="flex items-center gap-2">
                              <span className="text-lg">{getFlag(visit.country_code)}</span>
                              <span className="text-white font-medium">{visit.country}</span>
                              {visit.city && visit.city !== "Unknown" && (
                                <Badge variant="outline" className="text-[#94A3B8] border-white/10 text-xs">
                                  {visit.city}
                                </Badge>
                              )}
                            </span>
                          </td>
                          <td className="py-3">
                            <div className="flex flex-wrap gap-1 max-w-[220px]">
                              {(visit.pages_visited || [visit.page]).slice(0, 2).map((p, j) => (
                                <Badge 
                                  key={j} 
                                  className="bg-[#4F46E5]/20 text-[#A5B4FC] border border-[#4F46E5]/30 text-xs"
                                >
                                  {p === "/" ? "Home" : p.replace(/^\//, "").slice(0, 15)}
                                </Badge>
                              ))}
                              {(visit.pages_visited?.length || 1) > 2 && (
                                <Badge className="bg-[#F7931A]/20 text-[#F7931A] border border-[#F7931A]/30 text-xs">
                                  +{visit.pages_visited.length - 2}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3">
                            {/* Scroll percentage */}
                            {(() => {
                              const maxScroll = visit.max_scroll || 0;
                              const scrollColor = maxScroll >= 100 
                                ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                : maxScroll >= 75
                                  ? 'bg-[#F7931A]/20 text-[#F7931A] border-[#F7931A]/30'
                                  : maxScroll >= 50
                                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                    : maxScroll >= 25
                                      ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                                      : 'bg-white/10 text-white/40 border-white/10';
                              
                              return (
                                <div className="flex items-center gap-1">
                                  <MousePointer2 className={`w-3 h-3 ${maxScroll >= 75 ? 'text-green-400' : maxScroll >= 50 ? 'text-yellow-400' : 'text-white/40'}`} />
                                  <Badge className={`text-xs font-bold border ${scrollColor}`}>
                                    {maxScroll > 0 ? `${maxScroll}%` : '-'}
                                  </Badge>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="py-3">
                            {(() => {
                              const visitorType = detectVisitorType(visit);
                              return (
                                <div className="flex items-center gap-2">
                                  {visit.total_time_spent > 0 ? (
                                    <Badge className={`text-xs font-bold ${
                                      visit.total_time_spent > 120 
                                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                        : visit.total_time_spent > 30 
                                          ? 'bg-[#F7931A]/20 text-[#F7931A] border border-[#F7931A]/30' 
                                          : 'bg-white/10 text-white/60 border border-white/10'
                                    }`}>
                                      {formatTimeSpent(visit.total_time_spent)}
                                    </Badge>
                                  ) : (
                                    <span className="text-[#64748B] text-xs">-</span>
                                  )}
                                  <Badge className={`text-[10px] border ${visitorType.color}`}>
                                    {visitorType.type === "bot" && "🤖 "} 
                                    {visitorType.type === "admin" && "👑 "} 
                                    {visitorType.type === "safari" && "🍎 "} 
                                    {visitorType.label}
                                  </Badge>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-1.5">
                              <DeviceIcon device={visit.device} />
                              <span className="text-white/70 text-xs">
                                {visit.device} / {visit.browser}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 pr-3 text-white/60 text-xs">
                            {new Date(visit.timestamp).toLocaleString("ro-RO", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-[#94A3B8]">
                          {recentVisits.length > 0 
                            ? `Nicio vizită găsită pentru perioada "${periodFilter === 'today' ? 'Azi' : periodFilter === 'week' ? 'Săptămâna' : periodFilter === 'month' ? 'Luna' : 'Selectată'}"`
                            : "Încă nu sunt vizite înregistrate"
                          }
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Google Analytics Placeholder */}
          <Card className="bg-white/5 border-white/10 border-dashed">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F7931A]/20 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-[#F7931A]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Google Analytics</h3>
                    <p className="text-[#94A3B8] text-sm">Integrare disponibilă pentru statistici avansate</p>
                  </div>
                </div>
                <Badge className="bg-[#F7931A]/20 text-[#F7931A] border-[#F7931A]/30">
                  În Curând
                </Badge>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Încă nu sunt date</h3>
            <p className="text-[#94A3B8]">Statisticile vor apărea când site-ul va avea trafic</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
